/**
 * useCanvasState Hook - Canvas state management with Settings sync
 * Primary store: Preferences/Settings system (cross-device, Accord-aligned)
 * Fallback: IndexedDB (fast local read when Settings unavailable)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { UserId } from '@maco144/shared';
import {
  PanelLayoutStore,
  type PanelState,
  type PanelLayout,
  type PanelType,
} from './panel-layout-store';
import { snapPositionToGrid, snapSizeToGrid, constrainToViewport } from './grid-utils';
import { getDefaultLayout } from './default-layout';
import { getPanelEntry } from './panel-registry';
import type { GlobalPreferencesContextType } from './use-global-preferences';

/** Preference key for cross-device layout sync */
const LAYOUT_PREF_KEY = 'spatial-canvas.layout';

/** Layout data stored in the Settings system */
interface SyncedLayout {
  panels: PanelState[];
  nextZIndex: number;
  gridSize: number;
  savedAt: number;
}

export interface CanvasStateResult {
  panels: Map<string, PanelState>;
  gridSize: number;
  nextZIndex: number;
  isLoading: boolean;
  addPanel: (type: PanelType, position?: { x: number; y: number }) => void;
  movePanel: (id: string, position: { x: number; y: number }) => void;
  resizePanel: (id: string, size: { width: number; height: number }) => void;
  closePanel: (id: string) => void;
  minimizePanel: (id: string) => void;
  bringToFront: (id: string) => void;
  resetLayout: () => void;
  setGridSize: (size: number) => void;
  setPanels: (panels: Map<string, PanelState>) => void;
}

/**
 * React hook for managing spatial canvas state
 * Uses the Settings/Preferences system as primary store (cross-device sync)
 * Falls back to IndexedDB when Settings unavailable
 */
export function useCanvasState(
  userId: UserId,
  preferences?: GlobalPreferencesContextType | null
): CanvasStateResult {
  const [panels, setPanels] = useState<Map<string, PanelState>>(new Map());
  const [gridSize, setGridSize] = useState(20);
  const [nextZIndex, setNextZIndex] = useState(1000);
  const [isLoading, setIsLoading] = useState(true);

  // IndexedDB store - fallback when Settings unavailable
  const storeRef = useRef<PanelLayoutStore | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const localSavedAtRef = useRef<number>(0);
  const skipNextSaveRef = useRef(false);

  /**
   * Initialize: load from IndexedDB for fast local read
   */
  useEffect(() => {
    const initializeStore = async () => {
      try {
        const store = new PanelLayoutStore(userId);
        await store.init();
        storeRef.current = store;

        const persisted = await store.loadLayout();
        if (persisted) {
          const panelMap = new Map(persisted.panels.map((p) => [p.id, p]));
          setPanels(panelMap);
          setNextZIndex(persisted.nextZIndex);
          setGridSize(persisted.gridSize);
          localSavedAtRef.current = persisted.savedAt || 0;
        } else {
          setPanels(getDefaultLayout());
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Failed to initialize panel layout store:', error);
        setPanels(getDefaultLayout());
        setIsLoading(false);
      }
    };

    initializeStore();

    return () => {
      storeRef.current?.close();
    };
  }, [userId]);

  /**
   * Debounced auto-save
   * Settings (primary): writes to preferences system which handles local cache + server sync
   * IndexedDB (fallback): direct write when Settings unavailable
   */
  useEffect(() => {
    if (isLoading) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (panels.size === 0) return;

      // Skip if this state change came from applying a synced layout
      if (skipNextSaveRef.current) {
        skipNextSaveRef.current = false;
        return;
      }

      const savedAt = Date.now();
      const panelsArray = Array.from(panels.values());

      if (preferences?.isReady) {
        // Primary: save via Settings system
        localSavedAtRef.current = savedAt;
        preferences.preferences
          .setPreference(LAYOUT_PREF_KEY, {
            panels: panelsArray,
            nextZIndex,
            gridSize,
            savedAt,
          } as SyncedLayout)
          .catch((e) => console.warn('Failed to save layout to Settings:', e));
      } else if (storeRef.current) {
        // Fallback: save to IndexedDB directly
        storeRef.current
          .saveLayout({ userId, panels: panelsArray, nextZIndex, gridSize, savedAt })
          .catch((e) => console.error('Failed to save panel layout:', e));
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [panels, nextZIndex, gridSize, userId, isLoading, preferences?.isReady]);

  /**
   * Settings sync: poll for incoming layout changes from other devices
   * Runs immediately on mount (handles initial sync) then every 5s
   * Also migrates existing IndexedDB data to Settings on first connect
   */
  useEffect(() => {
    if (isLoading || !preferences?.isReady) return;

    const checkForUpdates = async () => {
      try {
        const serverValue = await preferences.preferences.getPreference(LAYOUT_PREF_KEY);
        const serverLayout = serverValue as SyncedLayout | undefined;

        if (serverLayout?.savedAt && serverLayout.savedAt > localSavedAtRef.current) {
          // Server has newer layout (from another device or initial load)
          const panelMap = new Map(serverLayout.panels.map((p) => [p.id, p]));
          setPanels(panelMap);
          setNextZIndex(serverLayout.nextZIndex);
          setGridSize(serverLayout.gridSize);
          localSavedAtRef.current = serverLayout.savedAt;
          skipNextSaveRef.current = true;

          // Cache to IndexedDB for fast local reads on next load
          if (storeRef.current) {
            storeRef.current
              .saveLayout({
                userId,
                panels: serverLayout.panels,
                nextZIndex: serverLayout.nextZIndex,
                gridSize: serverLayout.gridSize,
                savedAt: serverLayout.savedAt,
              })
              .catch(() => {});
          }
        } else if (localSavedAtRef.current > 0 && !serverLayout?.savedAt) {
          // Settings empty but we have local data - migrate IndexedDB → Settings
          const localLayout = await storeRef.current?.loadLayout();
          if (localLayout) {
            localSavedAtRef.current = localLayout.savedAt || Date.now();
            preferences.preferences
              .setPreference(LAYOUT_PREF_KEY, {
                panels: localLayout.panels,
                nextZIndex: localLayout.nextZIndex,
                gridSize: localLayout.gridSize,
                savedAt: localSavedAtRef.current,
              } as SyncedLayout)
              .catch(() => {});
          }
        }
      } catch {
        // Silently ignore poll errors
      }
    };

    // Immediate check on mount (initial sync + migration)
    checkForUpdates();

    // Then poll for cross-device changes
    const interval = setInterval(checkForUpdates, 5000);
    return () => clearInterval(interval);
  }, [isLoading, preferences?.isReady, userId]);

  /**
   * Handle window resize - re-constrain panels to viewport
   */
  useEffect(() => {
    const handleWindowResize = () => {
      setPanels((prev) => {
        const resized = new Map(prev);
        for (const [id, panel] of prev) {
          const constrained = constrainToViewport(
            panel.position,
            panel.size,
            window.innerWidth,
            window.innerHeight
          );
          resized.set(id, { ...panel, position: constrained });
        }
        return resized;
      });
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  /**
   * Add a new panel
   */
  const addPanel = useCallback(
    (type: PanelType, position?: { x: number; y: number }) => {
      const id = `${type}-${Date.now()}`;
      const entry = getPanelEntry(type);

      const newPanel: PanelState = {
        id,
        type,
        position: position || { x: 100 + panels.size * 30, y: 150 + panels.size * 30 },
        size: entry.defaultSize,
        zIndex: nextZIndex,
        minimized: false,
      };

      setPanels((prev) => new Map(prev).set(id, newPanel));
      setNextZIndex((prev) => prev + 1);
    },
    [panels.size, nextZIndex]
  );

  /**
   * Move a panel
   */
  const movePanel = useCallback(
    (id: string, position: { x: number; y: number }) => {
      const snapped = snapPositionToGrid(position, gridSize);
      setPanels((prev) => {
        const panel = prev.get(id);
        if (!panel) return prev;

        return new Map(prev).set(id, { ...panel, position: snapped });
      });
    },
    [gridSize]
  );

  /**
   * Resize a panel
   */
  const resizePanel = useCallback(
    (id: string, size: { width: number; height: number }) => {
      const snapped = snapSizeToGrid(size, gridSize);
      setPanels((prev) => {
        const panel = prev.get(id);
        if (!panel) return prev;

        return new Map(prev).set(id, { ...panel, size: snapped });
      });
    },
    [gridSize]
  );

  /**
   * Close (remove) a panel
   */
  const closePanel = useCallback((id: string) => {
    setPanels((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  /**
   * Minimize/toggle panel visibility
   */
  const minimizePanel = useCallback((id: string) => {
    setPanels((prev) => {
      const panel = prev.get(id);
      if (!panel) return prev;

      return new Map(prev).set(id, { ...panel, minimized: !panel.minimized });
    });
  }, []);

  /**
   * Bring panel to front (increase z-index)
   */
  const bringToFront = useCallback(
    (id: string) => {
      setPanels((prev) => {
        const panel = prev.get(id);
        if (!panel) return prev;

        return new Map(prev).set(id, { ...panel, zIndex: nextZIndex });
      });
      setNextZIndex((prev) => prev + 1);
    },
    [nextZIndex]
  );

  /**
   * Reset layout to default
   */
  const resetLayout = useCallback(() => {
    setPanels(getDefaultLayout());
    setNextZIndex(1000);
    setGridSize(20);
  }, []);

  return {
    panels,
    gridSize,
    nextZIndex,
    isLoading,
    addPanel,
    movePanel,
    resizePanel,
    closePanel,
    minimizePanel,
    bringToFront,
    resetLayout,
    setGridSize,
    setPanels,
  };
}
